import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { Form, FormErrorLabel, Modal, TextField } from '@app-velocity/ui';
import { useI18n } from '@app-velocity/i18n';
import { ClassValidatorResolver } from '@app-velocity/validators';
import { MessageBus } from '@app-velocity/message-bus';

import { useUserData } from '@app-velocity/libs/auth';

import { ModalWindowControls } from '../ModalWindowControls';
import { useUpdateUserData } from '../../../../hooks';
import { UserChangePasswordDto } from '../../../../dto/UserChangePasswordDto';

const messageBus = new MessageBus('users');

export const ChangePasswordModalContent = () => {
  const { userId } = useUserData();
  const { t } = useI18n();

  const methods = useForm<UserChangePasswordDto>({
    mode: 'onChange',
    resolver: ClassValidatorResolver(UserChangePasswordDto),
  });

  const { update, isLoading, error, isUpdateSuccess } = useUpdateUserData();

  const { handleSubmit, control } = methods;

  const onSubmit = (data: UserChangePasswordDto) => {
    if (!userId) return;

    update({
      updatePassword: {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      },
    });
  };

  useEffect(() => {
    if (!isUpdateSuccess) return;

    messageBus.publish('modal:user-settings:open', { tab: 'profile' });
  }, [isUpdateSuccess]);

  return (
    <Modal data-testid="change-password-modal">
      <Modal.Header title="users:settings.change_password" />
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Content>
            <Form.FieldContainer>
              <Form.Label>{t('users:settings.old_password')}</Form.Label>
              <TextField
                type="password"
                control={control}
                name="oldPassword"
                placeholder={t('users:settings.old_password_placeholder')}
                fullWidth={true}
              />
            </Form.FieldContainer>

            <Form.FieldContainer>
              <Form.Label>{t('users:settings.new_password')}</Form.Label>
              <TextField
                type="password"
                control={control}
                name="newPassword"
                placeholder={t('users:settings.new_password_placeholder')}
                fullWidth={true}
              />
            </Form.FieldContainer>

            <Form.FieldContainer>
              <Form.Label>{t('users:settings.confirm_password')}</Form.Label>
              <TextField
                type="password"
                control={control}
                name="confirmPassword"
                placeholder={t('users:settings.confirm_password_placeholder')}
                fullWidth={true}
              />
            </Form.FieldContainer>
          </Modal.Content>
          <Modal.Controls>
            {error && (
              <FormErrorLabel
                error={new Error(t('users:settings.error_saving_user_data'))}
              />
            )}
            <ModalWindowControls isLoading={isLoading} />
          </Modal.Controls>
        </Form>
      </FormProvider>
    </Modal>
  );
};
